import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import { useI18n } from '../lib/i18n';

export default function Login() {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Password reset. We always report success, even when the address has no
  // account — telling a stranger which emails are registered is an account
  // enumeration leak.
  async function onReset() {
    setError(null);
    setNotice(null);
    if (!email.trim()) {
      setError(t('login.reset.needEmail'));
      return;
    }
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
    } catch {
      /* swallowed on purpose — see comment above */
    } finally {
      setNotice(t('login.reset.sent'));
      setBusy(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/');
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? '';
      setError(
        code.includes('invalid') || code.includes('wrong') || code.includes('user-not-found')
          ? t('login.error.invalid')
          : t('login.error.generic'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setError(null);
    setBusy(true);
    try {
      await signInWithPopup(auth, googleProvider);
      navigate('/');
    } catch {
      setError(t('login.error.generic'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden p-4"
      style={{
        background:
          'radial-gradient(600px 320px at 82% 8%, rgba(34,197,94,.12), transparent 60%), radial-gradient(700px 360px at 10% 30%, rgba(30,80,160,.18), transparent 60%), #0a1628',
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(120,160,210,.10) 1px, transparent 1px), linear-gradient(90deg, rgba(120,160,210,.10) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage: 'radial-gradient(circle at 60% 20%, #000, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(circle at 60% 20%, #000, transparent 75%)',
        }}
        aria-hidden
      />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img src="/watag-mark.svg" alt="WaTag" className="h-10 w-10" />
            <div>
              {/* This header sits on the dark #0a1628 canvas, not on a card.
                  `navy-ink` was remapped to dark slate in the WhatChimp restyle,
                  which made both lines invisible here — keep them explicitly light. */}
              <div className="font-extrabold text-white">{t('app.name')}</div>
              <div className="text-xs text-white/70">{t('app.tagline')}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
            className="rounded-full bg-white/10 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/20"
          >
            {t('lang.switch')}
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-card shadow-2xl p-7">
          <h1 className="text-xl font-extrabold text-ink mb-1">{t('login.title')}</h1>
          <p className="text-sm text-ink-3 mb-6">{t('login.subtitle')}</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-ink-2 mb-1">
                {t('login.email')}
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary-50"
              />
            </div>
            <div>
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <label htmlFor="password" className="block text-sm font-semibold text-ink-2">
                  {t('login.password')}
                </label>
                <button
                  type="button"
                  onClick={onReset}
                  disabled={busy}
                  className="text-xs font-semibold text-primary hover:underline disabled:opacity-60"
                >
                  {t('login.reset.link')}
                </button>
              </div>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary-50"
              />
            </div>

            {error && (
              <p role="alert" className="text-sm font-semibold text-red-600">
                {error}
              </p>
            )}
            {notice && (
              <p role="status" className="text-sm font-semibold text-primary">
                {notice}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-primary hover:bg-primary-600 text-white font-bold rounded-lg py-2.5 disabled:opacity-60"
            >
              {busy ? t('login.loading') : t('login.submit')}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <span className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-ink-3 font-semibold">{t('login.or')}</span>
            <span className="flex-1 h-px bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={onGoogle}
            disabled={busy}
            className="w-full border border-slate-300 hover:bg-surface text-ink font-bold rounded-lg py-2.5 disabled:opacity-60"
          >
            {t('login.google')}
          </button>

          <p className="mt-6 text-center text-sm text-ink-3">
            {t('login.noAccount')}{' '}
            <Link to="/signup" className="font-bold text-primary hover:underline">
              {t('login.createAccount')}
            </Link>
          </p>
        </div>

        <p className="mt-5 text-[11px] leading-relaxed text-white/70 text-center px-2">
          {t('app.disclaimer')}
        </p>
        {/* The policies are static files in admin/public — `/privacy` (no
            extension) is swallowed by the SPA rewrite and bounces to the
            dashboard, so link the real documents. */}
        <p className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1 text-[11px] font-semibold">
          <a href="/privacy.html" className="text-accent hover:underline">
            {t('login.privacy')}
          </a>
          <span className="text-white/50" aria-hidden>
            ·
          </span>
          <a href="/terms.html" className="text-accent hover:underline">
            {t('login.terms')}
          </a>
        </p>
      </div>
    </div>
  );
}
